import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { shipmentsAPI } from '../services/api';
import Header from '../components/Header';
import Navigation from '../components/Navigation';
import ShipmentDetailModal from '../components/ShipmentDetailModal';
import './Dashboard.css';

function Dashboard() {
  const [shipments, setShipments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState({ type: '', text: '' });
  const [selectedShipment, setSelectedShipment] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchShipments();
  }, []);

  // Handle shipment query parameter from email link
  useEffect(() => {
    const shipmentId = searchParams.get('shipment');
    if (shipmentId && shipments.length > 0) {
      const shipment = shipments.find(s => s.id === parseInt(shipmentId));
      if (shipment) {
        handleView(shipment);
        // Remove query parameter after opening modal
        setSearchParams({});
      }
    }
  }, [shipments, searchParams]);

  const fetchShipments = async () => {
    try {
      const response = await shipmentsAPI.getAll();
      setShipments(response.data.shipments || []);
    } catch (err) {
      setError('Failed to load shipments');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleClone = async (shipment) => {
    try {
      // Fetch full shipment details
      const response = await shipmentsAPI.getById(shipment.id);
      const fullShipment = response.data.shipment;

      // Navigate to appropriate form based on shipment type
      if (fullShipment.shipment_type === 'international') {
        navigate('/new-intl-shipment', { state: { cloneData: fullShipment } });
      } else {
        navigate('/new-shipment', { state: { cloneData: fullShipment } });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to load shipment for cloning' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    }
  };

  const handleView = async (shipment) => {
    try {
      // Fetch full shipment details
      const response = await shipmentsAPI.getById(shipment.id);
      const fullShipment = response.data.shipment;
      setSelectedShipment(fullShipment);
      setShowDetailModal(true);
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to load shipment details' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    }
  };

  const handleEditResubmit = async (shipment) => {
    try {
      const response = await shipmentsAPI.getById(shipment.id);
      const fullShipment = response.data.shipment;
      const formPath = fullShipment.shipment_type === 'international'
        ? '/new-intl-shipment'
        : '/new-shipment';
      navigate(formPath, { state: { editData: fullShipment, editMode: true } });
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to load shipment for editing' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    }
  };

  const handleReject = () => {
    // Refresh shipments after rejection
    fetchShipments();
    setMessage({ type: 'success', text: 'Shipment rejected successfully' });
    setTimeout(() => setMessage({ type: '', text: '' }), 3000);
  };

  const closeDetailModal = () => {
    setShowDetailModal(false);
    setSelectedShipment(null);
  };

  return (
    <div className="app-container">
      <Header />
      <Navigation activeTab="my-shipments" />

      <div className="content">
        <h1 className="page-title">My Shipments</h1>

        {error && <div className="error-message">{error}</div>}
        {message.text && (
          <div className={message.type === 'error' ? 'error-message' : 'success-message'}>
            {message.text}
          </div>
        )}

        <div className="table-container">
          <table className="shipments-table">
            <thead>
              <tr>
                <th>Shipment ID</th>
                <th>Request Date</th>
                <th>Delivery Date</th>
                <th>City</th>
                <th>State</th>
                <th>Country</th>
                <th>PO</th>
                <th>Charge Code</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="10" className="loading-state">
                    <div className="spinner"></div>
                    <p>Loading your shipments...</p>
                  </td>
                </tr>
              ) : shipments.length === 0 ? (
                <tr>
                  <td colSpan="10" className="empty-state">
                    <div className="empty-state-content">
                      <p className="empty-state-title">No shipments yet</p>
                      <p className="empty-state-text">
                        Click "New Shipment" or "New Intl Shipment" above to create your first
                        shipment.
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                shipments.map((shipment) => (
                  <tr key={shipment.id}>
                    <td>
                      {shipment.id}
                      {shipment.attachment_count > 0 && (
                        <span title={`${shipment.attachment_count} attachment(s)`} style={{ marginLeft: '6px', opacity: 0.7 }}>📎</span>
                      )}
                    </td>
                    <td>{shipment.request_date || '-'}</td>
                    <td>{shipment.delivery_date || '-'}</td>
                    <td>{shipment.consignee_city || '-'}</td>
                    <td>{shipment.consignee_state || '-'}</td>
                    <td>{shipment.consignee_country || '-'}</td>
                    <td>{shipment.purchase_order || '-'}</td>
                    <td>{shipment.charge_code || '-'}</td>
                    <td>
                      {shipment.status && (
                        <span className={`badge-status ${shipment.status === 'rejected' ? 'status-badge-rejected' : shipment.status === 'resubmitted' ? 'status-badge-resubmitted' : shipment.status === 'approved' ? 'status-badge-approved' : shipment.status}`}>
                          {shipment.status === 'approved' ? `Approved` : shipment.status}
                        </span>
                      )}
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button className="view-button" onClick={() => handleView(shipment)}>
                          View
                        </button>
                        <button className="clone-button" onClick={() => handleClone(shipment)}>
                          Clone
                        </button>
                        {shipment.status === 'rejected' && (
                          <button className="resubmit-button" onClick={() => handleEditResubmit(shipment)}>
                            Edit &amp; Resubmit
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Shipment Detail Modal */}
        {showDetailModal && (
          <ShipmentDetailModal
            shipment={selectedShipment}
            onClose={closeDetailModal}
            onClone={handleClone}
            onReject={handleReject}
          />
        )}
      </div>
    </div>
  );
}

export default Dashboard;
